import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Users2, Loader2, AlertCircle, CheckCircle2, ChevronDown, ChevronUp, Sparkles, X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Settings → Player Cleanup card.
 *
 * Scans the user's leagues for duplicate-spelling player records and
 * lets them PERMANENTLY consolidate each group into a single canonical
 * spelling. Complements the runtime fuzzy merge — this one rewrites the
 * underlying DB records so CSV exports + historical game logs are clean.
 */
export default function PlayerCleanupCard() {
  const [scanning, setScanning] = useState(false);
  const [groups, setGroups] = useState(null); // null = not scanned yet
  const [expanded, setExpanded] = useState({}); // group_id → bool
  const [selectedCanonical, setSelectedCanonical] = useState({}); // group_id → name
  const [applying, setApplying] = useState({}); // group_id → bool
  const [dismissed, setDismissed] = useState({}); // group_id → bool

  const handleScan = useCallback(async () => {
    setScanning(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/player-merges/suggestions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status}: ${txt}`);
      }
      const data = await res.json();
      setGroups(data.groups || []);
      // Pre-pick the recommended canonical for each group
      const sel = {};
      (data.groups || []).forEach(g => {
        sel[g.id] = g.recommended_canonical?.name || '';
      });
      setSelectedCanonical(sel);
      setExpanded({});
      setDismissed({});
      toast.success(
        data.groups.length === 0
          ? 'Looking clean — no duplicate spellings detected.'
          : `Found ${data.groups.length} suggested merge${data.groups.length === 1 ? '' : 's'}.`,
      );
    } catch (e) {
      toast.error(`Scan failed: ${e.message}`);
    } finally {
      setScanning(false);
    }
  }, []);

  const handleApply = useCallback(async (group) => {
    const canonicalName = selectedCanonical[group.id];
    if (!canonicalName) {
      toast.error('Pick a canonical spelling first');
      return;
    }
    const canonical = group.candidates.find(c => c.name === canonicalName);
    if (!canonical) {
      toast.error('Selected canonical not found in candidates');
      return;
    }
    const variantNames = group.candidates
      .filter(c => c.name !== canonicalName)
      .map(c => c.name);

    setApplying(prev => ({ ...prev, [group.id]: true }));
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/player-merges/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          team_name: group.team_name,
          canonical_name: canonicalName,
          canonical_jersey: canonical.jersey,
          variant_names: variantNames,
          event_ids: group.event_ids?.length ? group.event_ids : null,
        }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status}: ${txt}`);
      }
      const data = await res.json();
      toast.success(
        `Merged → "${canonicalName}". Updated ${data.games_updated} game${data.games_updated === 1 ? '' : 's'} and ${data.rosters_updated} roster${data.rosters_updated === 1 ? '' : 's'}.`,
      );
      // Remove the group from the list
      setGroups(prev => (prev || []).filter(g => g.id !== group.id));
    } catch (e) {
      toast.error(`Merge failed: ${e.message}`);
    } finally {
      setApplying(prev => ({ ...prev, [group.id]: false }));
    }
  }, [selectedCanonical]);

  const visibleGroups = (groups || []).filter(g => !dismissed[g.id]);

  return (
    <Card className="border-slate-200" data-testid="settings-player-cleanup-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Users2 className="w-5 h-5 text-[#00247D]" />
          Player Cleanup
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Find players that appear with multiple spellings on the same team
          (e.g. <span className="font-mono text-[11px] bg-slate-100 px-1 rounded">Shennon Hana</span> vs <span className="font-mono text-[11px] bg-slate-100 px-1 rounded">Shennon Hanna</span>) and merge them
          permanently. Affects every shared schedule, leaderboard, and export.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            onClick={handleScan}
            disabled={scanning}
            className="bg-[#00247D] hover:bg-[#001A5C] text-white"
            data-testid="settings-scan-merges-btn"
          >
            {scanning ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning…</>
            ) : groups === null ? (
              <><Sparkles className="w-4 h-4 mr-2" /> Scan for duplicates</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> Re-scan</>
            )}
          </Button>
          {groups !== null && (
            <span className="text-xs text-slate-500" data-testid="settings-merges-count">
              {visibleGroups.length === 0
                ? 'No suggestions remaining.'
                : `${visibleGroups.length} suggestion${visibleGroups.length === 1 ? '' : 's'}`}
            </span>
          )}
        </div>

        {visibleGroups.length === 0 && groups !== null && (
          <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3" data-testid="settings-merges-empty">
            <CheckCircle2 className="w-4 h-4" />
            All clear — no duplicate spellings detected across your leagues.
          </div>
        )}

        <div className="space-y-2">
          {visibleGroups.map(group => {
            const isOpen = !!expanded[group.id];
            const isApplying = !!applying[group.id];
            const canonical = selectedCanonical[group.id] || group.recommended_canonical?.name;
            const recommended = group.recommended_canonical?.name;
            return (
              <div
                key={group.id}
                className="border border-slate-200 rounded-md overflow-hidden"
                data-testid={`merge-group-${group.id}`}
              >
                {/* Header row — clickable to expand */}
                <button
                  type="button"
                  onClick={() => setExpanded(prev => ({ ...prev, [group.id]: !isOpen }))}
                  className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors"
                  data-testid={`merge-group-toggle-${group.id}`}
                >
                  <div className="flex items-start gap-2 text-left min-w-0">
                    <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-800 truncate">
                        {group.team_name}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {group.candidates.length} spellings · jersey #{group.recommended_canonical?.jersey || '—'} · {group.candidates.reduce((s, c) => s + c.games_count, 0)} games total
                      </div>
                    </div>
                  </div>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
                </button>

                {/* Expanded detail */}
                {isOpen && (
                  <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 space-y-3" data-testid={`merge-group-detail-${group.id}`}>
                    <div className="text-xs text-slate-600">
                      Select the spelling to KEEP. All other variants will be rewritten to match.
                    </div>
                    <div className="space-y-1.5">
                      {group.candidates.map((c, idx) => {
                        const isCanonical = canonical === c.name;
                        const isRecommended = recommended === c.name;
                        return (
                          <label
                            key={`${c.name}-${idx}`}
                            className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer border transition-colors ${
                              isCanonical
                                ? 'bg-[#00247D] text-white border-[#00247D]'
                                : 'bg-white text-slate-700 border-slate-200 hover:border-[#00247D]'
                            }`}
                            data-testid={`merge-candidate-${group.id}-${idx}`}
                          >
                            <input
                              type="radio"
                              name={`canonical-${group.id}`}
                              value={c.name}
                              checked={isCanonical}
                              onChange={() => setSelectedCanonical(prev => ({ ...prev, [group.id]: c.name }))}
                              className="accent-amber-400"
                            />
                            <span className="font-medium flex-1 min-w-0 truncate">{c.name}</span>
                            <span className={`text-xs shrink-0 ${isCanonical ? 'text-amber-200' : 'text-slate-400'}`}>#{c.jersey || '—'}</span>
                            <span className={`text-xs shrink-0 ${isCanonical ? 'text-amber-200' : 'text-slate-500'}`}>{c.games_count} GP</span>
                            {isRecommended && (
                              <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 shrink-0 ${isCanonical ? 'bg-amber-400 text-[#00247D]' : 'bg-amber-100 text-amber-700'}`}>
                                Recommended
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setDismissed(prev => ({ ...prev, [group.id]: true }))}
                        disabled={isApplying}
                        data-testid={`merge-dismiss-${group.id}`}
                      >
                        <X className="w-3.5 h-3.5 mr-1" /> Dismiss
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => handleApply(group)}
                        disabled={isApplying || !canonical}
                        className="bg-[#00247D] hover:bg-[#001A5C] text-white"
                        data-testid={`merge-apply-${group.id}`}
                      >
                        {isApplying ? (
                          <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Merging…</>
                        ) : (
                          <>Merge into "{canonical}"</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
